// src/lib/portInClient.js
//
// Thin frontend client for the `phone-port-in` edge function. One function per
// dispatcher action. Each function returns `{data, error}` matching the
// supabase-js invoke convention so callers can `if (error) ...` consistently.
//
// The wire shape matches the Slice B1 locked API contract verbatim. See
// strategic-planning/PHONE-PORTING-PHASE-B-SLICE-B1-HANDOFF.md for the full
// request/response documentation.

import supabase from './supabaseClient';

async function invoke(body) {
  const { data, error } = await supabase.functions.invoke('phone-port-in', { body });
  return { data, error };
}

export function checkEligibility() {
  return invoke({ action: 'eligibility-check' });
}

export function createDraft() {
  return invoke({ action: 'create-draft' });
}

/**
 * Patch a draft. The wire format is whole-array replacement on `numbers`, so
 * callers must send the full intended numbers array each call. Sensitive
 * fields (`account_pin`, `account_number`) travel as plaintext and are
 * encrypted server-side before storage; the response strips vault IDs.
 */
export function updateDraft(portRequestId, patch) {
  return invoke({ action: 'update-draft', portRequestId, patch });
}

export function validateDraft(portRequestId) {
  return invoke({ action: 'validate', portRequestId });
}

export function portabilityCheck(phoneNumber) {
  return invoke({ action: 'portability-check', phoneNumber });
}

export function lookupCarrier(phoneNumber) {
  return invoke({ action: 'lookup-carrier', phoneNumber });
}

/**
 * Phase F: full carrier coaching bundle for a known carrier key. Returns
 * everything in _shared/carriers.ts's CarrierMetadata interface, including
 * screenshotPath, pinResetShortcut, accountNumberDigitCount, and
 * commonMistakes. Unknown keys fall through to the 'unknown' fallback rather
 * than a 4xx so Step 2 always has something to render.
 *
 * Response: { carrier: CarrierMetadata }
 */
export function lookupCarrierMetadata(carrierKey) {
  return invoke({ action: 'lookup-carrier-metadata', carrierKey });
}

export function listPortRequests({ status = null, limit = 50, offset = 0 } = {}) {
  const body = { action: 'list', limit, offset };
  if (status) body.status = status;
  return invoke(body);
}

export function getPortRequest(portRequestId) {
  return invoke({ action: 'get', portRequestId });
}

/**
 * Phase D: upload a carrier bill (PDF/PNG/JPEG, <=10MB). The wire shape is
 * multipart/form-data: action + portRequestId + file. supabase-js' invoke()
 * passes FormData straight through. Server-side magic-byte sniffs, writes to
 * the port-documents Storage bucket, forwards to Twilio LoadingDocuments, and
 * (if the LOA is already signed) auto-fires submit.
 *
 * Response: { success: true, twilioDocumentSid, storagePath, autoSubmitted, twilioPortInSid|null, autoSubmitReason }
 * Errors:
 *   400 { error }                                  file too large / wrong type / missing fields
 *   403 { error, blockers: [...] }                 eligibility lost
 *   409 { error }                                  port in a non-uploadable status
 *   500/502 { error }                              storage / Twilio failure
 */
export function uploadBill(portRequestId, file) {
  const form = new FormData();
  form.append('action', 'upload-bill');
  form.append('portRequestId', portRequestId);
  form.append('file', file);
  return supabase.functions.invoke('phone-port-in', { body: form });
}

/**
 * Phase D: submit the port request to Twilio's Porting API. Server re-runs
 * validatePortRequestForSubmit, decrypts the Vault secrets (PIN + account
 * number) in function-scope memory, POSTs to /v1/Porting/PortIns, captures
 * the returned SID, advances status -> 'submitted', and appends the
 * submitted_to_twilio event.
 *
 * Response: { success: true, twilioPortInSid }
 * Errors:
 *   400 { error, errors: [{field, message}, ...] }  validation failures
 *   400 { error, reason, fieldErrors }              Twilio 4xx (carrier rejected)
 *   403 { error, blockers: [...] }                  eligibility lost
 *   409 { error, reason }                           prerequisites unmet (LOA not signed, bill not uploaded, etc.)
 *   502 { error }                                   Twilio 5xx / network failure
 */
export function submitPortRequest(portRequestId) {
  return invoke({ action: 'submit', portRequestId });
}

/**
 * Phase E: cancel an in-flight port request. Server-side this calls Twilio
 * /v1/Porting/PortIn/{sid}/Cancel (if a Twilio PortIn SID exists),
 * transitions the row to status='canceled', and deletes the Vault secrets
 * for the request. Phase F (2026-05-17) removed the DocuSign-envelope-void
 * leg along with the rest of the DocuSign integration.
 *
 * `reason` is optional and lands in the port_request_events audit row.
 * Server caps it at 500 chars.
 *
 * Response: { success: true, portRequest, alreadyCanceled?, twilioCancelError }
 * Errors:
 *   400 { error }                                  missing portRequestId
 *   403 { error, blockers: [...] }                 eligibility lost
 *   409 { error, reason? }                         not in a cancelable status / past FOC date / mid-submit / stale_status
 *   500 { error }                                  DB transition failed
 */
export function cancelPortRequest(portRequestId, reason) {
  return invoke({ action: 'cancel', portRequestId, reason });
}
